import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { ko } from 'date-fns/locale';
import styled from 'styled-components';
import {
	CONTENT_TYPE_MAP,
	TContentType,
	IInfluencerDB,
	IRankingFilterParams,
} from 'type/tracking';

import { useGetInfluencers } from 'hooks/queries/tracking';
import CoreImg from 'shared/CoreImg';
import CoreSvg from 'shared/CoreSvg';
import Icon from 'shared/icons';
import { ReportSection, ReportSectionCard } from 'styles/styled-components/report';
import style from 'styles/styled-components/style';

import ContentRankingTable from './ContentRankingTable';
import CreatorRankingTable from './CreatorRankingTable';

interface IRankingSection {
	code: string;
	trackingStartDate?: string;
	trackingEndDate?: string;
}

const TAB_OPTIONS = ['콘텐츠 랭킹', '크리에이터 랭킹'];

const TRACKING_CONTENT_TYPES: TContentType[] = [
	'ig_feed',
	'ig_video',
	'yt_video',
	'yt_shorts',
];
const CONTENT_TYPE_OPTIONS = TRACKING_CONTENT_TYPES.map((key) => CONTENT_TYPE_MAP[key]);
const CONTENT_TYPE_REVERSE_MAP: Record<string, TContentType> = {};
TRACKING_CONTENT_TYPES.forEach((key) => {
	CONTENT_TYPE_REVERSE_MAP[CONTENT_TYPE_MAP[key]] = key;
});

// ====== 인플루언서 멀티셀렉트 드롭다운 ======
const PLATFORM_ICON_MAP: Record<string, JSX.Element> = {
	INSTAGRAM: <Icon.Insta />,
	YOUTUBE: <Icon.Youtube />,
	TIKTOK: <Icon.Tiktok />,
	THREADS: <Icon.Insta />,
};

const InfluencerSelect = ({
	creators,
	selected,
	onChange,
}: {
	creators: IInfluencerDB[];
	selected: string[];
	onChange: (codes: string[]) => void;
}) => {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState('');
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const filtered = creators.filter((c) =>
		(c.username ?? '').toLowerCase().includes((search ?? '').toLowerCase()),
	);

	const allSelected = selected.length === creators.length;

	const toggleAll = () => {
		if (allSelected) {
			onChange([]);
		} else {
			onChange(creators.map((c) => c.influencerCode));
		}
	};

	const toggleOne = (code: string) => {
		if (selected.includes(code)) {
			onChange(selected.filter((c) => c !== code));
		} else {
			onChange([...selected, code]);
		}
	};

	const label =
		selected.length === 0 || allSelected
			? '계정/채널 전체'
			: `계정/채널 ${selected.length}개`;

	return (
		<DropdownWrapper ref={ref}>
			<DropdownTrigger $active={open} onClick={() => setOpen(!open)}>
				<span>{label}</span>
				<DropdownIconWrap $open={open}>
					<CoreSvg _size="16px">
						<Icon.ArrowDown />
					</CoreSvg>
				</DropdownIconWrap>
			</DropdownTrigger>
			{open && (
				<DropdownPanel>
					<SearchInput
						type="text"
						placeholder="계정을 검색하세요."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
					<OptionList>
						{!search && (
							<OptionItem onClick={toggleAll}>
								<Checkbox $checked={allSelected} $primary={false} />
								<span>전체 선택</span>
							</OptionItem>
						)}
						{filtered.map((creator: IInfluencerDB) => {
							const checked = selected.includes(creator.influencerCode);
							return (
								<OptionItem key={creator.influencerCode} onClick={() => toggleOne(creator.influencerCode)}>
									<Checkbox $checked={checked} $primary />
									<AvatarWithBadge>
										<AvatarSmall>
											<CoreImg
												src={creator.profileImageUrl ?? ''}
												alt=""
												_width={28}
												_height={28}
												error_src="https://featuring-score-web.s3.amazonaws.com/static/images/score/v3/common/cmmn-default-image.png"
											/>
										</AvatarSmall>
										{PLATFORM_ICON_MAP[creator.platformType] && (
											<PlatformBadge>
												<CoreSvg _size="10px">
													{PLATFORM_ICON_MAP[creator.platformType]}
												</CoreSvg>
											</PlatformBadge>
										)}
									</AvatarWithBadge>
									<span>{creator.username}</span>
								</OptionItem>
							);
						})}
					</OptionList>
					<DropdownFooter>
						<CancelBtn onClick={() => setOpen(false)}>취소</CancelBtn>
					</DropdownFooter>
				</DropdownPanel>
			)}
		</DropdownWrapper>
	);
};

// ====== 콘텐츠 유형 멀티셀렉트 드롭다운 ======
const CONTENT_TYPE_ICON: Record<string, JSX.Element> = {
	ig_feed: <Icon.Insta />,
	ig_video: <Icon.InstaReels />,
	yt_video: <Icon.Youtube />,
	yt_shorts: <Icon.YoutubeShorts />,
};

const ContentTypeSelect = ({
	selected,
	onChange,
}: {
	selected: TContentType[];
	onChange: (types: TContentType[]) => void;
}) => {
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const allSelected = selected.length === TRACKING_CONTENT_TYPES.length || selected.length === 0;

	const toggleAll = () => {
		if (allSelected) {
			onChange([]);
		} else {
			onChange([...TRACKING_CONTENT_TYPES]);
		}
	};

	const toggleOne = (type: TContentType) => {
		if (selected.includes(type)) {
			onChange(selected.filter((t) => t !== type));
		} else {
			onChange([...selected, type]);
		}
	};

	const label =
		selected.length === 0 || allSelected
			? '콘텐츠 유형 전체'
			: `콘텐츠 유형 ${selected.length}개`;

	return (
		<DropdownWrapper ref={ref}>
			<DropdownTrigger $active={open} onClick={() => setOpen(!open)}>
				<span>{label}</span>
				<DropdownIconWrap $open={open}>
					<CoreSvg _size="16px">
						<Icon.ArrowDown />
					</CoreSvg>
				</DropdownIconWrap>
			</DropdownTrigger>
			{open && (
				<DropdownPanel>
					<OptionList>
						<OptionItem onClick={toggleAll}>
							<Checkbox $checked={allSelected} $primary={false} />
							<span>전체 선택</span>
						</OptionItem>
						{TRACKING_CONTENT_TYPES.map((type) => {
							const checked = selected.includes(type);
							return (
								<OptionItem key={type} onClick={() => toggleOne(type)}>
									<Checkbox $checked={checked} $primary />
									{CONTENT_TYPE_ICON[type] && (
										<CoreSvg _size="16px">{CONTENT_TYPE_ICON[type]}</CoreSvg>
									)}
									<span>{CONTENT_TYPE_MAP[type]}</span>
								</OptionItem>
							);
						})}
					</OptionList>
					<DropdownFooter>
						<CancelBtn onClick={() => setOpen(false)}>닫기</CancelBtn>
					</DropdownFooter>
				</DropdownPanel>
			)}
		</DropdownWrapper>
	);
};

// ====== 메인 컴포넌트 ======
const RankingSection = ({ code, trackingStartDate, trackingEndDate }: IRankingSection) => {
	const { data: influencers } = useGetInfluencers(code);
	const [activeTab, setActiveTab] = useState('콘텐츠 랭킹');
	const [selectedContentTypes, setSelectedContentTypes] = useState<TContentType[]>([...TRACKING_CONTENT_TYPES]);
	const [startDate, setStartDate] = useState<Date | null>(null);
	const [endDate, setEndDate] = useState<Date | null>(null);
	const [minFollowersDisplay, setMinFollowersDisplay] = useState('');
	const [maxFollowersDisplay, setMaxFollowersDisplay] = useState('');
	const [minFollowers, setMinFollowers] = useState('');
	const [maxFollowers, setMaxFollowers] = useState('');
	const [selectedCreators, setSelectedCreators] = useState<string[]>([]);

	const minDate = trackingStartDate ? new Date(trackingStartDate) : undefined;
	const maxDate = trackingEndDate ? new Date(trackingEndDate) : undefined;

	const minDebounceRef = useRef<ReturnType<typeof setTimeout>>();
	const maxDebounceRef = useRef<ReturnType<typeof setTimeout>>();

	const handleMinChange = useCallback((val: string) => {
		setMinFollowersDisplay(val);
		if (minDebounceRef.current) clearTimeout(minDebounceRef.current);
		minDebounceRef.current = setTimeout(() => setMinFollowers(val), 500);
	}, []);

	const handleMaxChange = useCallback((val: string) => {
		setMaxFollowersDisplay(val);
		if (maxDebounceRef.current) clearTimeout(maxDebounceRef.current);
		maxDebounceRef.current = setTimeout(() => setMaxFollowers(val), 500);
	}, []);

	useEffect(() => {
		return () => {
			if (minDebounceRef.current) clearTimeout(minDebounceRef.current);
			if (maxDebounceRef.current) clearTimeout(maxDebounceRef.current);
		};
	}, []);

	const formatDateStr = (d: Date | null) => {
		if (!d) return '';
		const yyyy = d.getFullYear();
		const mm = String(d.getMonth() + 1).padStart(2, '0');
		const dd = String(d.getDate()).padStart(2, '0');
		return `${yyyy}-${mm}-${dd}`;
	};

	const appliedFilter = useMemo(() => {
		const filter: IRankingFilterParams = {};
		const startStr = formatDateStr(startDate);
		const endStr = formatDateStr(endDate);
		if (startStr) filter.startDate = startStr;
		if (endStr) filter.endDate = endStr;
		if (selectedContentTypes.length > 0 && selectedContentTypes.length < TRACKING_CONTENT_TYPES.length) {
			filter.contentTypes = selectedContentTypes;
		}
		if (minFollowers) filter.followerMin = Number(minFollowers);
		if (maxFollowers) filter.followerMax = Number(maxFollowers);
		if (selectedCreators.length > 0 && selectedCreators.length < (influencers?.length ?? 0)) {
			filter.influencerCode = selectedCreators;
		}
		return filter;
	}, [
		selectedContentTypes,
		startDate,
		endDate,
		minFollowers,
		maxFollowers,
		selectedCreators,
		influencers,
	]);

	return (
		<ReportSection _margin="0">
			<ReportSectionCard _padding="24px">
				<FilterArea>
					<FilterRow>
						<FilterGroup>
							<DatePickerWrapper>
								<DatePicker
									selectsRange
									startDate={startDate}
									endDate={endDate}
									onChange={(dates: [Date | null, Date | null]) => {
										const [start, end] = dates;
										setStartDate(start);
										setEndDate(end);
									}}
									dateFormat="yyyy.MM.dd"
									placeholderText="기간 선택"
									locale={ko}
									isClearable
									minDate={minDate}
									maxDate={maxDate}
								/>
							</DatePickerWrapper>
						</FilterGroup>

						<FilterGroup>
							<ContentTypeSelect
								selected={selectedContentTypes}
								onChange={setSelectedContentTypes}
							/>
						</FilterGroup>

						<FollowerGroup>
							<FollowerLabel>팔로워</FollowerLabel>
							<NumberInput
								type="text"
								inputMode="numeric"
								placeholder="최솟값"
								value={
									minFollowersDisplay ? Number(minFollowersDisplay).toLocaleString() : ''
								}
								onChange={(e) => {
									const raw = e.target.value.replace(/,/g, '');
									if (raw === '' || /^\d+$/.test(raw)) handleMinChange(raw);
								}}
							/>
							<Tilde>~</Tilde>
							<NumberInput
								type="text"
								inputMode="numeric"
								placeholder="최댓값"
								value={
									maxFollowersDisplay ? Number(maxFollowersDisplay).toLocaleString() : ''
								}
								onChange={(e) => {
									const raw = e.target.value.replace(/,/g, '');
									if (raw === '' || /^\d+$/.test(raw)) handleMaxChange(raw);
								}}
							/>
						</FollowerGroup>

						<FilterGroup>
							<InfluencerSelect
								creators={influencers ?? []}
								selected={selectedCreators}
								onChange={setSelectedCreators}
							/>
						</FilterGroup>
					</FilterRow>
				</FilterArea>

				<TabRow>
					{TAB_OPTIONS.map((tab) => (
						<Tab key={tab} $active={activeTab === tab} onClick={() => setActiveTab(tab)}>
							{tab}
						</Tab>
					))}
				</TabRow>

				{activeTab === '콘텐츠 랭킹' ? (
					<ContentRankingTable code={code} filter={appliedFilter} />
				) : (
					<CreatorRankingTable code={code} filter={appliedFilter} />
				)}
			</ReportSectionCard>
		</ReportSection>
	);
};

// ====== Styled Components ======

const FilterArea = styled.div`
	margin-bottom: 16px;
`;

const FilterRow = styled.div`
	display: flex;
	flex-wrap: nowrap;
	gap: 12px;
	align-items: center;
	position: relative;
	z-index: 10;
`;

const FilterGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex-shrink: 0;
`;

const DatePickerWrapper = styled.div`
	.react-datepicker-wrapper {
		width: 220px;
	}

	.react-datepicker__input-container input {
		width: 100%;
		height: 36px;
		padding: 0 10px;
		border: 1px solid ${({ theme }) => theme.colors.gray.gray15};
		border-radius: 8px;
		${({ theme }) => theme.typo.body.span};
		color: ${({ theme }) => theme.colors.gray.gray80_text};
		outline: none;
		background: transparent;
		box-sizing: border-box;

		&:focus {
			border: 2px solid ${({ theme }) => theme.colors.uplus.primary50_key};
			padding: 0 9px;
		}

		&::placeholder {
			color: ${({ theme }) => theme.colors.gray.gray20};
		}
	}

	.react-datepicker__close-icon::after {
		background-color: ${({ theme }) => theme.colors.gray.gray30};
		font-size: 12px;
	}

	.react-datepicker {
		border: 1px solid ${({ theme }) => theme.colors.gray.gray15};
		box-shadow: ${({ theme }) => theme.shadow.depth02};
		font-family: inherit;
	}

	.react-datepicker__header {
		background-color: ${({ theme }) => theme.colors.gray.gray05_bg};
		border-bottom: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	}

	.react-datepicker__day--selected,
	.react-datepicker__day--range-start,
	.react-datepicker__day--range-end,
	.react-datepicker__day--keyboard-selected {
		background-color: ${({ theme }) => theme.colors.uplus.primary50_key};
		color: #fff;

		&:hover {
			background-color: ${({ theme }) => theme.colors.uplus.primary50_key};
			opacity: 0.85;
		}
	}

	.react-datepicker__day:hover {
		background-color: ${({ theme }) => theme.colors.uplus.primary80_darken};
		color: #fff;
	}

	.react-datepicker__day--in-range,
	.react-datepicker__day--in-selecting-range {
		background-color: ${({ theme }) => theme.colors.uplus.primary50_key};
		color: #fff;

		&:hover {
			background-color: ${({ theme }) => theme.colors.uplus.primary80_darken};
			color: #fff;
		}
	}

	.react-datepicker__day--in-selecting-range:not(.react-datepicker__day--in-range) {
		background-color: ${({ theme }) => theme.colors.uplus.primary50_key};
		color: #fff;
	}
`;

const FollowerGroup = styled.div`
	display: flex;
	align-items: center;
	height: 36px;
	border: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	border-radius: 8px;
	padding: 0 10px;
	gap: 4px;
	flex-shrink: 0;
	box-sizing: border-box;

	&:focus-within {
		border: 2px solid ${({ theme }) => theme.colors.uplus.primary50_key};
		padding: 0 9px;
	}
`;

const FollowerLabel = styled.span`
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray50};
	white-space: nowrap;
	margin-right: 4px;
`;

const Tilde = styled.span`
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray30};
`;

const NumberInput = styled.input`
	width: 80px;
	border: none;
	outline: none;
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray80_text};
	background: transparent;
	text-align: center;

	&::-webkit-inner-spin-button,
	&::-webkit-outer-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	&::placeholder {
		color: ${({ theme }) => theme.colors.gray.gray30};
	}
`;

const TabRow = styled.div`
	display: flex;
	border-bottom: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	margin-bottom: 16px;
`;

const Tab = styled.button<{ $active: boolean }>`
	padding: 10px 16px;
	border: none;
	background: none;
	cursor: pointer;
	${({ theme }) => theme.typo.body.p};
	color: ${({ $active, theme }) =>
		$active ? theme.colors.gray.gray80_text : theme.colors.gray.gray30};
	font-weight: ${({ $active }) => ($active ? 700 : 400)};
	border-bottom: 2px solid
		${({ $active, theme }) => ($active ? theme.colors.gray.gray80_text : 'transparent')};
	margin-bottom: -1px;
	transition: color 0.15s, border-color 0.15s;

	&:hover {
		color: ${({ theme }) => theme.colors.gray.gray80_text};
	}
`;

// ====== 인플루언서 드롭다운 스타일 ======

const DropdownWrapper = styled.div`
	position: relative;
`;

const DropdownTrigger = styled.button<{ $active: boolean }>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 4px;
	height: 36px;
	min-width: 160px;
	padding: 0 12px;
	border: ${({ $active, theme }) =>
		$active
			? `2px solid ${theme.colors.uplus.primary50_key}`
			: `1px solid ${theme.colors.gray.gray15}`};
	border-radius: 8px;
	background: ${({ theme }) => theme.colors.gray.gray_white};
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray80_text};
	cursor: pointer;
	white-space: nowrap;
`;

const DropdownIconWrap = styled.span<{ $open: boolean }>`
	display: flex;
	align-items: center;
	transition: transform 0.15s;
	transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
`;

const DropdownPanel = styled.div`
	position: absolute;
	top: calc(100% + 4px);
	left: 0;
	width: 240px;
	background: ${({ theme }) => theme.colors.gray.gray_white};
	border: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	border-radius: 8px;
	box-shadow: ${({ theme }) => theme.shadow.depth02};
	z-index: 100;
	display: flex;
	flex-direction: column;
`;

const SearchInput = styled.input`
	margin: 12px 12px 8px;
	padding: 6px 10px;
	border: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	border-radius: 6px;
	${({ theme }) => theme.typo.body.span};
	outline: none;

	&:focus {
		border-color: ${({ theme }) => theme.colors.uplus.primary50_key};
	}

	&::placeholder {
		color: ${({ theme }) => theme.colors.gray.gray30};
	}
`;

const OptionList = styled.div`
	max-height: 240px;
	overflow-y: auto;
`;

const OptionItem = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	cursor: pointer;
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray80_text};

	&:hover {
		background-color: ${({ theme }) => theme.colors.gray.gray05_bg};
	}
`;

const Checkbox = styled.div<{
	$checked: boolean;
	$primary: boolean;
}>`
	width: 18px;
	height: 18px;
	border-radius: 4px;
	border: 1.5px solid
		${({ $checked, $primary, theme }) =>
			$checked && $primary
				? theme.colors.uplus.primary50_key
				: $checked
				? theme.colors.gray.gray50
				: theme.colors.gray.gray30};
	background-color: ${({ $checked, $primary, theme }) =>
		$checked && $primary
			? theme.colors.uplus.primary50_key
			: $checked
			? theme.colors.gray.gray50
			: 'transparent'};
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;

	&::after {
		content: ${({ $checked }) => ($checked ? "'✓'" : "''")};
		color: #fff;
		font-size: 11px;
		font-weight: 700;
	}
`;

const AvatarWithBadge = styled.div`
	position: relative;
	width: 28px;
	height: 28px;
	flex-shrink: 0;
`;

const AvatarSmall = styled.div`
	width: 28px;
	height: 28px;
	border-radius: 50%;
	overflow: hidden;
`;

const PlatformBadge = styled.div`
	position: absolute;
	bottom: -2px;
	right: -2px;
	width: 14px;
	height: 14px;
	border-radius: 50%;
	background: white;
	display: flex;
	align-items: center;
	justify-content: center;
	border: 1px solid ${({ theme }) => theme.colors.gray.gray15};
`;

const DropdownFooter = styled.div`
	display: flex;
	justify-content: flex-end;
	padding: 8px 12px;
	border-top: 1px solid ${({ theme }) => theme.colors.gray.gray15};
`;

const CancelBtn = styled.button`
	border: none;
	background: none;
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray50};
	cursor: pointer;

	&:hover {
		color: ${({ theme }) => theme.colors.gray.gray80_text};
	}
`;

const { Text } = style;
// Text 사용을 위해 남김 (eslint unused 방지)
void Text;

export default RankingSection;
